import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';
import { ADDONS } from '../../lib/pricing';
import { DELIVERY_FEE, FREE_DELIVERY_THRESHOLD } from '../../lib/pricing';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

// ── Single add-on card with quantity stepper ──────────────────────────────────
function AddonCard({ addon, quantity, onIncrement, onDecrement }) {
  const isSelected = quantity > 0;
  const maxQty = addon.max_quantity || 5;
  const canIncrement = quantity < maxQty;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className={`glass-panel p-5 border-2 transition-all duration-300 flex flex-col items-center text-center space-y-3 ${
        isSelected
          ? 'border-blush-300 ring-4 ring-blush-100 bg-blush-50/10'
          : 'border-transparent hover:border-sage-100'
      }`}
    >
      <div className="w-full aspect-square rounded-2xl bg-warmwhite overflow-hidden flex items-center justify-center p-3">
        <img
          src={addon.image}
          alt={addon.name}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `<div class="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center"><span class="text-sage-400 text-2xl">🍽️</span></div>`;
          }}
        />
      </div>

      <div className="space-y-0.5">
        <h3 className="font-serif text-lg text-charcoal">{addon.name}</h3>
        <p className="text-xs text-sage-400">{addon.description}</p>
      </div>

      <div className="flex items-center justify-between w-full pt-2 border-t border-sage-50">
        <span className="text-blush-500 font-bold">${addon.price.toFixed(2)}</span>

        <div className="flex items-center gap-2">
          <button
            onClick={onDecrement}
            disabled={quantity === 0}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all text-sm font-bold ${
              quantity > 0
                ? 'bg-blush-300 text-white hover:bg-blush-400 shadow-sm'
                : 'bg-sage-100 text-sage-300 cursor-not-allowed'
            }`}
          >
            <SafeIcon name="Minus" />
          </button>

          <span className={`w-6 text-center font-bold text-sm ${isSelected ? 'text-charcoal' : 'text-sage-300'}`}>
            {quantity}
          </span>

          <button
            onClick={onIncrement}
            disabled={!canIncrement}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all text-sm font-bold ${
              canIncrement
                ? 'bg-sage-100 text-sage-500 hover:bg-blush-100 hover:text-blush-400 shadow-sm'
                : 'bg-sage-100 text-sage-300 cursor-not-allowed opacity-50'
            }`}
          >
            <SafeIcon name="Plus" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Step ─────────────────────────────────────────────────────────────────
export default function Step2Addons({ formData, updateFormData, onNext, onBack }) {
  const [liveAddons, setLiveAddons] = React.useState([]);
  const [loadingAddons, setLoadingAddons] = React.useState(true);

  React.useEffect(() => {
    async function fetchAddons() {
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from('addons')
            .select('*')
            .eq('available', true)
            .order('sort_order');
          if (!error && data && data.length > 0) {
            // Normalise DB shape to match the shape AddonCard expects
            setLiveAddons(data.map(a => ({
              ...a,
              image: a.image || a.image_url || '',
              description: a.description || '',
            })));
            setLoadingAddons(false);
            return;
          }
        } catch (e) { /* fall through to mock */ }
      }
      setLiveAddons(ADDONS);
      setLoadingAddons(false);
    }
    fetchAddons();
  }, []);

  const addonsList = liveAddons;

  // addons stored as array of { id, name, price, image, desc, qty }
  const selectedAddons = formData.addons || [];

  const getQty = (addonId) => {
    const found = selectedAddons.find(a => a.id === addonId);
    return found ? found.qty : 0;
  };

  const updateAddonQty = (addon, delta) => {
    const current = selectedAddons.find(a => a.id === addon.id);
    const currentQty = current ? current.qty : 0;
    const maxQty = addon.max_quantity || 5;
    const newQty = Math.max(0, Math.min(maxQty, currentQty + delta));

    let updated;
    if (newQty === 0) {
      updated = selectedAddons.filter(a => a.id !== addon.id);
    } else if (current) {
      updated = selectedAddons.map(a =>
        a.id === addon.id ? { ...a, qty: newQty } : a
      );
    } else {
      updated = [...selectedAddons, { ...addon, qty: newQty }];
    }
    updateFormData({ addons: updated });
  };

  const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price * a.qty, 0);
  const productPrice = formData.size?.price || formData.product?.base_price || 0;
  const subtotal = productPrice + addonsTotal;
  const toFreeDelivery = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal);
  const isUnlocked = toFreeDelivery === 0;
  const progressPct = Math.min(100, Math.round((subtotal / FREE_DELIVERY_THRESHOLD) * 100));

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-10"
    >
      <div className="text-center space-y-3">
        <h2 className="text-4xl font-serif text-charcoal">Add-ons</h2>
        <p className="text-sage-500 text-lg font-medium max-w-2xl mx-auto">
          Sauces, sides & extras — choose what you'd like to add.
        </p>
      </div>

      {loadingAddons && (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blush-300" /></div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {addonsList.map((addon) => (
          <AddonCard
            key={addon.id}
            addon={addon}
            quantity={getQty(addon.id)}
            onIncrement={() => updateAddonQty(addon, 1)}
            onDecrement={() => updateAddonQty(addon, -1)}
          />
        ))}
      </div>

      {/* Free delivery progress bar */}
      <motion.div
        layout
        className={`rounded-3xl p-6 space-y-4 border-2 transition-all duration-500 ${
          isUnlocked ? 'bg-green-50 border-green-200' : 'bg-sage-50 border-sage-100'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-colors duration-500 ${
              isUnlocked ? 'bg-green-500 text-white' : 'bg-white text-blush-300'
            }`}>
              <SafeIcon name={isUnlocked ? 'Truck' : 'ShoppingBag'} />
            </div>
            <p className={`font-medium transition-colors duration-300 ${isUnlocked ? 'text-green-700' : 'text-sage-600'}`}>
              {selectedAddons.length === 0
                ? 'No add-ons selected yet.'
                : `${selectedAddons.reduce((s, a) => s + a.qty, 0)} item${selectedAddons.reduce((s, a) => s + a.qty, 0) !== 1 ? 's' : ''} added.`}
            </p>
          </div>
          {addonsTotal > 0 && (
            <span className="font-serif text-2xl text-charcoal">+ ${addonsTotal.toFixed(2)}</span>
          )}
        </div>

        <div className="space-y-2">
          <div className="w-full bg-white rounded-full h-3 overflow-hidden shadow-inner border border-sage-100">
            <motion.div
              className={`h-full rounded-full transition-colors duration-500 ${isUnlocked ? 'bg-green-400' : 'bg-blush-300'}`}
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
          {isUnlocked ? (
            <motion.p
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm font-bold text-green-600 flex items-center gap-2"
            >
              <SafeIcon name="CheckCircle" className="text-green-500 shrink-0" />
              Free delivery unlocked!
            </motion.p>
          ) : (
            <p className="text-sm text-sage-500">
              Add{' '}
              <span className="font-bold text-blush-500">${toFreeDelivery.toFixed(2)}</span>
              {' '}more to unlock{' '}
              <span className="font-bold text-charcoal">free delivery</span>
              {' '}— otherwise{' '}
              <span className="font-medium">${DELIVERY_FEE} applies</span>.
            </p>
          )}
        </div>
      </motion.div>

      <NavButtons
        onNext={onNext}
        onBack={onBack}
        nextLabel="Delivery Details"
      />
    </motion.div>
  );
}
